/**
 * Redirect safety utilities — open redirect prevention.
 *
 * Two redirect patterns exist in KeeperFA:
 *
 *   1. Post-login SPA navigation (from OAuth `state` param after silent SSO or
 *      full login) — must always be a relative path within this app.
 *      Use: isSafeRelativePath()
 *
 *   2. Third-party IdP redirect_uri (partner sites redirecting users through
 *      KeeperFA as identity provider) — must match the domain allowlist.
 *      Use: isSafeExternalUrl()
 *
 * Add new partner domains to ALLOWED_REDIRECT_DOMAINS when onboarding them.
 */

/**
 * Football Australia partner domains allowed as redirect_uri targets.
 *
 * Only EXACT hostname matches are accepted — wildcard subdomain matching
 * is intentionally disabled to prevent attackers from registering a new
 * subdomain (e.g. learnx.stg.footballaustralia.com.au) and passing
 * validation via a parent domain entry.
 *
 * If a new subdomain needs to be allowed, add it explicitly here AND in
 * ALLOWED_REDIRECT_DOMAINS in Auth.php (keep both lists in sync).
 *
 * Only HTTPS is accepted — HTTP entries would be ignored even if listed.
 */
export const ALLOWED_REDIRECT_DOMAINS = [
  'footballaustralia.com.au',
  'socceroos.com.au',
  'matildas.com.au',
  'ourgameaus.com.au',
  'playfootball.com.au',
  'pararoos.com.au',
  'paramatildas.com.au',
  'nationalfutsalchampionships.com.au',
  'nationalpremierleagues.com.au',
  'australiacup.com.au',
  'myfootball.com.au',
  'australianchampionship.com.au',
  'footballwest.com.au',
  'footballvictoria.com.au',
  'footballsa.com.au',
  'northernnswfootball.com.au',
  'footballtasmania.com.au',
  'learn.footballaustralia.com.au',
  'rego.staging.letsplayfootball.au',
  'rego.qa.letsplayfootball.au',
  'registration.playfootball.com.au',
  'stglearning.footballaustralia.com.au',
  'testapp.stg.ffa.football',
  'testapp.dev.ffa.football',
  'testapp.ffa.football',
  'api.oneclickservices.com.au',
];

/**
 * Return true when `path` is a safe relative path for internal SPA navigation.
 *
 * Accepts:
 *   "/my-account"
 *   "/my-account/settings"
 *   "/callback-oneclick?token=abc123"
 *
 * Rejects:
 *   "https://evil.com"   — absolute URL (contains "://")
 *   "//evil.com"         — protocol-relative URL
 *   "javascript:alert()" — non-path scheme
 *   ""                   — empty string
 *   null / undefined     — non-string
 *
 * @param {string} path
 * @returns {boolean}
 */
export function isSafeRelativePath(path) {
  return (
    typeof path === 'string' &&
    path.length > 0 &&
    path.startsWith('/') &&
    !path.startsWith('//') &&
    !path.includes('://')
  );
}

/**
 * Return true when `url` is an absolute HTTPS URL whose hostname exactly
 * matches an entry in ALLOWED_REDIRECT_DOMAINS.
 *
 * Wildcard subdomain matching is intentionally NOT supported — every
 * permitted hostname must be listed explicitly. This prevents an attacker
 * from registering a new subdomain of an allowed parent domain and using
 * it as an open redirect target.
 *
 * Examples (allowlist contains "footballaustralia.com.au"):
 *   ✓ https://footballaustralia.com.au/
 *   ✗ https://www.footballaustralia.com.au/   (not listed — rejected)
 *   ✗ https://learnx.stg.footballaustralia.com.au/  (attacker subdomain — rejected)
 *   ✗ https://evilfootballaustralia.com.au/   (prefix attack — rejected)
 *   ✗ http://footballaustralia.com.au/         (not HTTPS — rejected)
 *
 * @param {string} url - Absolute URL to validate
 * @returns {boolean}
 */
export function isSafeExternalUrl(url) {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:') return false;
    const hostname = parsed.hostname.toLowerCase();
    return ALLOWED_REDIRECT_DOMAINS.some((domain) => hostname === domain);
  } catch {
    // URL constructor throws on malformed input — treat as unsafe
    return false;
  }
}
